export const NOTIFICATION_EVENT = "venpro:notifications-changed";

export const notificationEventTypes = [
  "job.assignment",
  "job.schedule",
  "job.status",
  "job.cancelled",
  "invoice.created",
  "invoice.sent",
  "payment.recorded",
  "dealership.connection",
];

export const defaultNotificationPreferences = {
  channels: { inApp: true, email: true, push: true, sms: false },
  events: Object.fromEntries(notificationEventTypes.map((type) => [type, true])),
  smsConsent: false,
  updatedAt: null,
};

function safeOrganizationId(organizationId) {
  return String(organizationId ?? "").replace(/[^A-Za-z0-9_-]/g, "").slice(0, 128) || "unknown";
}

function notificationsKey(organizationId) {
  return `venpro_demo_notifications_${safeOrganizationId(organizationId)}`;
}

function preferencesKey(organizationId) {
  return `venpro_demo_notification_preferences_${safeOrganizationId(organizationId)}`;
}

function seedNotifications() {
  const now = Date.now();
  return [
    {
      id: "notification_demo_job_cancelled",
      type: "job.cancelled",
      title: "Job cancelled",
      message: "An assigned job was cancelled. Open it to review the reason.",
      priority: "high",
      entityType: "job",
      entityId: "job_demo_1042",
      route: "/jobs/job_demo_1042",
      status: "unread",
      delivery: { inApp: "delivered", email: "pending", push: "pending", sms: "disabled" },
      createdAt: new Date(now - 12 * 60_000).toISOString(),
      readAt: null,
      retentionClass: "operational",
      legalHold: false,
    },
    {
      id: "notification_demo_invoice_sent",
      type: "invoice.sent",
      title: "Invoice sent",
      message: "An invoice was marked sent and remains available in receivables.",
      priority: "normal",
      entityType: "invoice",
      entityId: "invoice-demo-1008",
      route: "/invoices",
      status: "unread",
      delivery: { inApp: "delivered", email: "pending", push: "pending", sms: "disabled" },
      createdAt: new Date(now - 2 * 60 * 60_000).toISOString(),
      readAt: null,
      retentionClass: "operational",
      legalHold: false,
    },
    {
      id: "notification_demo_dealership",
      type: "dealership.connection",
      title: "Dealership connection changed",
      message: "A dealership connection request or status changed.",
      priority: "normal",
      entityType: "dealership",
      entityId: "dealer-demo-pacific",
      route: "/dealerships",
      status: "read",
      delivery: { inApp: "delivered", email: "disabled", push: "disabled", sms: "disabled" },
      createdAt: new Date(now - 26 * 60 * 60_000).toISOString(),
      readAt: new Date(now - 24 * 60 * 60_000).toISOString(),
      retentionClass: "operational",
      legalHold: false,
    },
  ];
}

function notify(organizationId) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(NOTIFICATION_EVENT, { detail: { organizationId: safeOrganizationId(organizationId) } }));
}

function writeJson(key, value) {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(key, JSON.stringify(value));
}

export function readDemoNotifications(organizationId) {
  if (typeof localStorage === "undefined") return seedNotifications();
  const key = notificationsKey(organizationId);
  try {
    const saved = JSON.parse(localStorage.getItem(key) || "null");
    if (Array.isArray(saved)) return saved;
  } catch {
    // Replace malformed demo-only state with fictitious defaults.
  }
  const seeded = seedNotifications();
  writeJson(key, seeded);
  return seeded;
}

export function publishDemoNotification(organizationId, input) {
  if (!organizationId) return null;
  const now = new Date().toISOString();
  const notification = {
    id: `notification_demo_${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}_${Math.random().toString(36).slice(2)}`}`,
    type: notificationEventTypes.includes(input?.type) ? input.type : "job.status",
    title: String(input?.title ?? "Operational update").slice(0, 120),
    message: String(input?.message ?? "Open VenPro to review this update.").slice(0, 240),
    priority: input?.priority === "high" ? "high" : "normal",
    entityType: input?.entityType ?? null,
    entityId: input?.entityId ?? null,
    route: input?.route ?? null,
    status: "unread",
    delivery: { inApp: "delivered", email: "pending", push: "pending", sms: "disabled" },
    createdAt: now,
    readAt: null,
    retentionClass: "operational",
    legalHold: false,
  };
  writeJson(notificationsKey(organizationId), [notification, ...readDemoNotifications(organizationId)].slice(0, 100));
  notify(organizationId);
  return notification;
}

export function markDemoNotificationRead(organizationId, notificationId, read = true) {
  const now = new Date().toISOString();
  const next = readDemoNotifications(organizationId).map((notification) => notification.id === notificationId
    ? { ...notification, status: read ? "read" : "unread", readAt: read ? now : null }
    : notification);
  writeJson(notificationsKey(organizationId), next);
  notify(organizationId);
  return next.find((notification) => notification.id === notificationId) ?? null;
}

export function markAllDemoNotificationsRead(organizationId) {
  const now = new Date().toISOString();
  const current = readDemoNotifications(organizationId);
  const next = current.map((notification) => notification.status === "read" ? notification : { ...notification, status: "read", readAt: now });
  writeJson(notificationsKey(organizationId), next);
  notify(organizationId);
  return current.filter((notification) => notification.status !== "read").length;
}

export function readDemoNotificationPreferences(organizationId) {
  if (typeof localStorage === "undefined") return structuredClone(defaultNotificationPreferences);
  try {
    const saved = JSON.parse(localStorage.getItem(preferencesKey(organizationId)) || "null");
    if (saved?.channels && saved?.events) return saved;
  } catch {
    // Replace malformed demo-only state with safe defaults.
  }
  return structuredClone(defaultNotificationPreferences);
}

export function saveDemoNotificationPreferences(organizationId, preferences) {
  const next = { ...preferences, channels: { ...preferences.channels, inApp: true }, updatedAt: new Date().toISOString() };
  writeJson(preferencesKey(organizationId), next);
  notify(organizationId);
  return next;
}
